import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { useAxios, useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { type FC, useCallback, useEffect, useReducer, useState } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { useAppState } from '@/capacitor/hooks/useAppState'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { getBlobFromUri, getFilenameFromBlob } from '@/modules/shared/helpers/file-upload'
import { useToast } from '@/modules/shared/hooks/useToast'
import { CameraNotAvailable } from './CameraNotAvailable'

import type { CameraPermissionState, PermissionStatus, Photo } from '@capacitor/camera'
import type { HbFile, QuestionAnswerInput } from '@healthblocks-io/core'
import type { QuestionnaireQuestionWithHealthParameters } from 'index'

export type Props = {
  data: QuestionnaireQuestionWithHealthParameters
  answer?: QuestionAnswerInput

  onPhotoChange: (
    /** The id of the file that was uploaded in Healthblocks (null if skipped) */
    fileId: number | null,
    /** Indicate if the photo was uploaded or skipped */
    state: 'upload' | 'skip',
  ) => void | Promise<void>
  onNext: () => void
}

enum FileUploadStatus {
  Initial = 'initial',
  Preview = 'preview',
}

type State = {
  status: FileUploadStatus
  previewFile: Photo | null
  hbFileId: number | null
}

type Action =
  | { type: 'SET_PREVIEW'; previewFile: Photo; fileId?: number }
  | { type: 'UPLOAD_FILE'; previewFile: Photo; fileId: number }
  | { type: 'CONTINUE_WITHOUT_FILE' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_PREVIEW': {
      return {
        status: FileUploadStatus.Preview,
        previewFile: action.previewFile,
        hbFileId: action.fileId ?? null,
      }
    }
    case 'UPLOAD_FILE': {
      return {
        status: FileUploadStatus.Preview,
        previewFile: action.previewFile,
        hbFileId: action.fileId,
      }
    }
    case 'CONTINUE_WITHOUT_FILE': {
      return {
        status: FileUploadStatus.Initial,
        previewFile: null,
        hbFileId: null,
      }
    }
    default: {
      return state
    }
  }
}

export const QuestionFileUpload: FC<Props> = ({ data, answer, onPhotoChange, onNext }: Props) => {
  // State
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>({
    camera: Capacitor.isNativePlatform() ? 'prompt' : 'granted',
    photos: Capacitor.isNativePlatform() ? 'prompt' : 'granted',
  })

  const [state, dispatch] = useReducer(reducer, {
    status: FileUploadStatus.Initial,
    previewFile: null,
    hbFileId: null,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Hooks
  const member = useSubjectMember()
  const axios = useAxios()
  const { active } = useAppState()
  const { showErrorToast } = useToast()

  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()

  // Functions
  // ---------------------------------------- //
  /**
   * Check if the user has granted permission to use the camera or photo library
   */
  const checkPermissions = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      return {
        camera: 'granted' as CameraPermissionState,
        photos: 'granted' as CameraPermissionState,
      }
    }

    let newStatus: PermissionStatus = await Camera.checkPermissions()
    setPermissionStatus(newStatus)

    if (newStatus.camera === 'prompt' || newStatus.photos === 'prompt') {
      newStatus = await Camera.requestPermissions()
      setPermissionStatus(newStatus)
    }

    return newStatus
  }, [])

  const takePhoto = async () => {
    if (!Capacitor.isNativePlatform()) {
      // Handle file upload for desktop
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'

      input.addEventListener('change', async (event: any) => {
        const file = event.target.files[0]
        if (file) {
          const webPath = URL.createObjectURL(file)
          dispatch({
            type: 'SET_PREVIEW',
            previewFile: {
              webPath,
              format: file.type.split('/')[1],
            } as Photo,
          })
        }
      })

      input.click()
      return
    }

    try {
      const permissions = await checkPermissions()

      // Determine the best source to upload a file
      // Web -> photo library
      // Native -> prompt or available source (camera, photos)
      let availableSource: CameraSource
      if (hasPermission(permissions.camera) && hasPermission(permissions.photos)) {
        availableSource = CameraSource.Prompt
      } else if (hasPermission(permissions.camera)) {
        availableSource = CameraSource.Camera
      } else if (hasPermission(permissions.photos)) {
        availableSource = CameraSource.Photos
      } else {
        // TODO: Translate this error message
        throw new Error("Toegang tot camera of foto's is vereist om een foto te uploaden")
      }

      const image = await Camera.getPhoto({
        source: availableSource,
        quality: 60,
        resultType: CameraResultType.Uri,
      })

      dispatch({ type: 'SET_PREVIEW', previewFile: image })
    } catch (error) {
      if (error instanceof Error) {
        let message = error.message

        if (!message) {
          message = t({
            id: 'questionnaire.file-upload.permission-error',
            comment: 'Camera permission error',
            message: 'Er is een fout opgetreden bij het uploaden van de foto',
          })
        }
        // Ignore user cancelled photos app
        else if (message.includes('User cancelled photos app')) {
          return
        }
        showErrorToast({
          message,
        })
      }

      console.error('Error taking photo', error)
    }
  }

  const confirmPhoto = async () => {
    if (!isSubmitting) {
      setIsSubmitting(true)
      try {
        if (state.hbFileId) {
          mixpanel.track('file_uploaded_kept', {
            id: state.hbFileId,
            url: state.previewFile?.webPath,
          })
          onPhotoChange(state.hbFileId, 'upload')
        } else if (state.previewFile?.webPath) {
          const blob = await getBlobFromUri(state.previewFile.webPath)
          const filename = getFilenameFromBlob(blob)

          const formData = new FormData()

          formData.append('file', blob, filename)

          if (data.fileDriver?.id) {
            formData.append('driverId', data.fileDriver.id.toString())
          }

          if (member?.id) {
            formData.append('memberId', member.id.toString())
          }

          const fileResponse = await axios.post<{
            id: number
            key: string
          }>('/file', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          mixpanel.track('file_uploaded', {
            id: fileResponse.data.id,
          })
          onPhotoChange(fileResponse.data.id, 'upload')
        } else {
          throw new Error('No photo to upload')
        }
      } catch (error) {
        mixpanel.track('file_upload_failed')
        console.error('Error uploading photo', error)
        showErrorToast({
          message: t({
            id: 'questionnaire.file-upload.error',
            comment: 'Error message for file upload',
            message: 'Er is een fout opgetreden bij het uploaden van de foto',
          }),
        })
      }

      setIsSubmitting(false)
    }
  }

  /**
   * Handle skipping the question
   * @param clearPhoto - Clear the photo and remove the file from the answer (if optional question)
   */
  const handleContinueWithoutFile = () => {
    dispatch({ type: 'CONTINUE_WITHOUT_FILE' })
    onPhotoChange(null, 'skip')
  }

  // Effects
  // ---------------------------------------- //
  // Check permissions
  useEffect(() => {
    if (active && Capacitor.isNativePlatform()) {
      checkPermissions()
    }
  }, [active, checkPermissions])

  // Preview file that is already uploaded in Healthblocks
  useEffect(() => {
    if (answer?.value?.fileIds?.[0]) {
      axios
        .get<HbFile & { url?: string }>(`/file/${answer.value.fileIds[0]}?include=url`)
        .then(async (response) => {
          // SAS URLs are self-authenticated via query params. The stream endpoint
          // throws for Azure SAS files, so use the URL directly when available.
          if (response.data.url?.includes('.blob.core.windows.net')) {
            dispatch({
              type: 'SET_PREVIEW',
              previewFile: {
                webPath: response.data.url,
                format: 'jpg',
              } as Photo,
              fileId: response.data.id,
            })
            return
          }

          const streamUrl = `/file/${response.data.key}/${response.data.projectId}/file`
          const blobResponse = await axios.get<Blob>(streamUrl, {
            responseType: 'blob',
          })
          dispatch({
            type: 'SET_PREVIEW',
            previewFile: {
              webPath: URL.createObjectURL(blobResponse.data),
              format: 'jpg',
            } as Photo,
            fileId: response.data.id,
          })
        })
        .catch((error) => {
          console.error('Error fetching file from Healthblocks', error)
        })
    }
  }, [answer, axios])

  // Render
  if (!hasPermission(permissionStatus.camera) && !hasPermission(permissionStatus.photos)) {
    return <CameraNotAvailable onNext={onNext} />
  }

  return (
    <StContainer>
      {state.status === FileUploadStatus.Preview ? <StPreview src={state.previewFile?.webPath} alt='' /> : null}

      <StActionContainer>
        {/* Button rules:
          Initial state (FileUploadStatus.Initial):
          - Always shows "Bestand toevoegen" as primary action
          - Shows "Ga verder zonder bestand" as secondary action only if question is not required

          Preview state (FileUploadStatus.Preview):
          - Always shows "Gebruik dit bestand" as primary action
          - Always shows "Nieuw bestand toevoegen" as secondary action
          - Shows "Ga verder zonder bestand" as additional secondary action only if question is not required
        */}
        {state.status === FileUploadStatus.Preview ? (
          <>
            <Button
              inline={!isMobile}
              variant='Primary'
              onClick={confirmPhoto}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t({
                id: 'questionnaire.file-upload.confirm-photo',
                message: 'Gebruik dit bestand',
              })}
            </Button>
            <Button
              inline={!isMobile}
              variant='Secondary'
              onClick={takePhoto}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t({
                id: 'questionnaire.file-upload.retake-photo',
                message: 'Nieuw bestand toevoegen',
              })}
            </Button>
            {!data.required && (
              <Button
                variant='Secondary'
                onClick={handleContinueWithoutFile}
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {t({
                  id: 'questionnaire.file-upload.skip',
                  message: 'Ga verder zonder bestand',
                })}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              inline={!isMobile}
              variant='Primary'
              onClick={takePhoto}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t({
                id: 'questionnaire.file-upload.take-photo',
                message: 'Bestand toevoegen',
              })}
            </Button>
            {!data.required && (
              <Button
                variant='Secondary'
                onClick={handleContinueWithoutFile}
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {t({
                  id: 'questionnaire.file-upload.skip',
                  message: 'Ga verder zonder bestand',
                })}
              </Button>
            )}
          </>
        )}
      </StActionContainer>
    </StContainer>
  )
}

const StContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StPreview = styled.img`
  flex: 1;
  object-fit: cover;

  margin: 0 ${({ theme }) => theme.UI.SpacingPx.Space4};
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space4};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  overflow: hidden;
`

const StActionContainer = styled.div`
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space4};
  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }

  @media ${breakpoints.s} {
    box-shadow: none;
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space15};
    display: flex;
    justify-content: flex-end;
  }
`

function hasPermission(permissionStatus: CameraPermissionState) {
  return permissionStatus === 'granted' || permissionStatus === 'limited'
}
